import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { works } from "@/mocks/works";

const slides = [...works].sort((a, b) => b.heat - a.heat).slice(0, 5);

export default function HeroCarousel() {
  const navigate = useNavigate();
  const [index, setIndex] = useState(0);
  const [keyword, setKeyword] = useState("");

  useEffect(() => {
    const timer = setInterval(() => setIndex((i) => (i + 1) % slides.length), 6500);
    return () => clearInterval(timer);
  }, []);

  const active = slides[index];

  const go = (dir: number) => setIndex((i) => (i + dir + slides.length) % slides.length);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <section className="relative h-[600px] w-full overflow-hidden bg-primary-950 md:h-[680px]">
      {slides.map((s, i) => (
        <div
          key={s.id}
          className={`absolute inset-0 transition-opacity duration-1000 ${i === index ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={s.heroImage}
            alt={`${s.title} 宣传海报`}
            title={`${s.title} 影视资讯`}
            className={`h-full w-full object-cover object-top ${i === index ? "animate-kenburns" : ""}`}
          />
        </div>
      ))}
      <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/70 to-primary-950/30" />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-950/85 via-primary-950/40 to-transparent" />

      <div className="relative mx-auto flex h-full w-full max-w-[1400px] flex-col justify-end px-4 pb-14 md:px-8 md:pb-20">
        <div key={active.id} className="max-w-2xl animate-fade-up">
          <div className="mb-4 flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-accent-500 px-2.5 py-1 text-xs font-bold text-primary-950">
              {active.type === "movie" ? "电影" : "电视剧"}
            </span>
            <span className="flex items-center gap-1 rounded-md bg-background-50/15 px-2.5 py-1 text-xs font-medium text-background-50">
              <i className="ri-star-fill text-accent-400" />
              {active.rating.toFixed(1)}
            </span>
            <span className="rounded-md bg-background-50/15 px-2.5 py-1 text-xs text-background-100">
              {active.year} · {active.region}
            </span>
          </div>
          <h1 className="font-heading text-3xl font-black leading-tight text-background-50 md:text-5xl">{active.title}</h1>
          <p className="mt-3 font-heading text-base italic text-accent-300 md:text-lg">「{active.tagline}」</p>
          <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            {active.synopsis}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${active.id}`}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-6 py-3 text-sm font-bold text-primary-950 transition-colors hover:bg-accent-400"
            >
              查看详情
              <i className="ri-arrow-right-line" />
            </Link>
            <Link
              to={`/category/${active.type}`}
              className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10"
            >
              更多{active.type === "movie" ? "电影" : "剧集"}
            </Link>
          </div>
        </div>

        <form
          onSubmit={handleSearch}
          className="mt-10 flex w-full max-w-xl flex-col gap-2 sm:flex-row sm:items-center sm:rounded-full sm:bg-background-50/10 sm:p-1.5"
        >
          <div className="relative flex-1">
            <i className="ri-search-line pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-base text-background-100" />
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              type="text"
              placeholder="搜索影视名称、演员或影评关键词"
              className="w-full rounded-full border border-background-50/20 bg-background-50/10 py-3 pl-11 pr-4 text-sm text-background-50 outline-none placeholder:text-background-200/70 focus:border-accent-400 sm:border-transparent sm:bg-transparent"
            />
          </div>
          <button
            type="submit"
            className="cursor-pointer whitespace-nowrap rounded-full bg-accent-500 px-7 py-3 text-sm font-bold text-primary-950 transition-colors hover:bg-accent-400"
          >
            搜索
          </button>
        </form>

        <div className="mt-8 flex items-center gap-5">
          <div className="flex items-center gap-2">
            {slides.map((s, i) => (
              <button
                key={s.id}
                type="button"
                aria-label={`切换到 ${s.title}`}
                onClick={() => setIndex(i)}
                className={`h-1.5 cursor-pointer rounded-full transition-all ${
                  i === index ? "w-8 bg-accent-500" : "w-3 bg-background-50/40 hover:bg-background-50/70"
                }`}
              />
            ))}
          </div>
          <div className="ml-auto hidden items-center gap-2 md:flex">
            <button
              type="button"
              aria-label="上一张"
              onClick={() => go(-1)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-arrow-left-line" />
            </button>
            <button
              type="button"
              aria-label="下一张"
              onClick={() => go(1)}
              className="flex h-10 w-10 cursor-pointer items-center justify-center rounded-full border border-background-50/30 text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-arrow-right-line" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}